import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Alert, Box, Button, Card, Checkbox, CircularProgress, FormControlLabel, Stack, Typography } from "@mui/material";
import { API } from "../constant/apiConstant";
import { Colors } from "../utils/colors";

export default function InfluencerInvitation() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState("");
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${API.BASE_URL}/api/influencer/invitation/${encodeURIComponent(token)}`, { signal: controller.signal })
      .then(async (response) => {
        const body = await response.json();
        if (!response.ok || !body.status) throw new Error(body.message || "Invitation is unavailable.");
        setInvitation(body.data);
      })
      .catch((cause) => { if (cause.name !== "AbortError") setError(cause.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [token]);

  const accept = async () => {
    if (!checked || busy) return;
    setBusy(true);
    setError("");
    try {
      const response = await fetch(`${API.BASE_URL}/api/influencer/invitation/${encodeURIComponent(token)}/accept`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accept_terms: true, terms_version: invitation.terms_version,
          terms_hash: invitation.terms_hash })
      });
      const body = await response.json();
      if (response.status === 401) {
        navigate(`/login?returnTo=${encodeURIComponent(window.location.pathname)}`);
        return;
      }
      if (!response.ok || !body.status) throw new Error(body.message || "Unable to accept this invitation.");
      navigate("/account/authmenu/account/influencer");
    } catch (cause) { setError(cause.message); }
    finally { setBusy(false); }
  };

  return <Box sx={{ minHeight: "80vh", bgcolor: Colors.secondary, px: { xs: 1, sm: 3 }, py: 4 }}>
    <Card sx={{ maxWidth: 900, mx: "auto", p: { xs: 2, sm: 4 }, bgcolor: "#092b49", color: "white", borderRadius: 3 }}>
      <Typography variant="h4" sx={{ color: Colors.accent, fontWeight: 800, mb: 2 }}>Influencer Programme Invitation</Typography>
      {loading && <CircularProgress sx={{ color: Colors.accent }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {invitation && <Stack spacing={2}>
        <Typography>Welcome, {invitation.first_name}. Review your offer and the complete Terms before accepting.</Typography>
        <Typography><strong>Commission:</strong> {invitation.commission_percent}% on eligible ticket purchases</Typography>
        <Typography><strong>Contract term:</strong> {invitation.term_months} months from acceptance</Typography>
        <Typography><strong>Terms effective date:</strong> {invitation.terms_effective_date}</Typography>
        <Typography variant="h6">{invitation.terms_title}</Typography>
        <Box component="section" aria-label="Influencer Terms and Conditions" sx={{ maxHeight: 480, overflowY: "auto", p: 2, bgcolor: "#f7f4e9", color: "#183449", borderRadius: 2 }}>
          <Typography component="pre" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", fontFamily: "inherit", fontSize: 14, lineHeight: 1.7, m: 0 }}>{invitation.terms_text}</Typography>
        </Box>
        <FormControlLabel control={<Checkbox checked={checked} onChange={(event) => setChecked(event.target.checked)} sx={{ color: Colors.accent }} />}
          label="I have read and agree to the Influencer Programme Terms and Conditions." />
        <Button variant="contained" disabled={!checked || busy} onClick={accept} sx={{ alignSelf: "flex-start" }}>
          {busy ? "Activating..." : "Accept Terms and Activate"}
        </Button>
      </Stack>}
    </Card>
  </Box>;
}
